import { useState, useEffect } from 'react';
import { testHubhusConnection, triggerHubhusSync, getHubhusConfig } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  ExternalLink,
  AlertCircle
} from 'lucide-react';

const AdminHubhusPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Form state for custom config test
  const [customConfig, setCustomConfig] = useState({
    api_base_url: '',
    api_token: ''
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await getHubhusConfig();
      setConfig(response.data);
      setCustomConfig({
        api_base_url: response.data.api_base_url || '',
        api_token: ''
      });
    } catch (error) {
      console.error('Failed to load config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      // Test with current config (from .env)
      const response = await testHubhusConnection();
      setTestResult(response.data);
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Test fejlede'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleTestCustomConfig = async () => {
    if (!customConfig.api_base_url || !customConfig.api_token) {
      setTestResult({
        success: false,
        message: 'Udfyld både URL og token'
      });
      return;
    }

    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await testHubhusConnection(customConfig);
      setTestResult(response.data);
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Test fejlede'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await triggerHubhusSync();
      setTestResult({
        success: true,
        message: 'Sync startet - tjek logs for status'
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Sync fejlede'
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 font-outfit">Hubhus Integration</h1>
        <p className="text-slate-500 mt-1">Konfigurer forbindelse til Hubhus API</p>
      </div>

      {/* Current Config Status */}
      <Card className="border-slate-100 mb-6">
        <CardHeader>
          <CardTitle className="text-lg font-outfit">Aktuel konfiguration</CardTitle>
          <CardDescription>Indstillinger fra server miljø</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="space-y-3">
              <div className="h-5 w-48 bg-slate-100 rounded animate-pulse" />
              <div className="h-5 w-32 bg-slate-100 rounded animate-pulse" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">API URL</span>
                <span className="font-mono text-sm">{config?.api_base_url || 'Ikke konfigureret'}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">API Token</span>
                <Badge className={config?.api_token_set 
                  ? 'bg-emerald-50 text-emerald-700' 
                  : 'bg-amber-50 text-amber-700'
                }>
                  {config?.api_token_set ? 'Konfigureret' : 'Mangler'}
                </Badge>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={handleTestConnection}
                  disabled={testing}
                  variant="outline"
                  className="flex-1"
                  data-testid="test-connection-btn"
                >
                  {testing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4 mr-2" />
                  )}
                  Test forbindelse
                </Button>
                
                <Button 
                  onClick={handleSync}
                  disabled={syncing}
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  data-testid="sync-now-btn"
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Sync nu
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Test Result */}
      {testResult && (
        <Card className={`border mb-6 ${testResult.success ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              {testResult.success ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={`font-medium ${testResult.success ? 'text-emerald-800' : 'text-red-800'}`}>
                  {testResult.message}
                </p>
                
                {testResult.sample_data && testResult.sample_data.length > 0 && (
                  <div className="mt-3 p-3 bg-white rounded-lg">
                    <p className="text-xs text-slate-500 mb-2">Eksempel data:</p>
                    <pre className="text-xs text-slate-700 overflow-auto max-h-40">
                      {JSON.stringify(testResult.sample_data[0], null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Custom Config */}
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-outfit">Test anden konfiguration</CardTitle>
          <CardDescription>
            Test forbindelse med andre credentials (gemmes ikke)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="api_url">API Base URL</Label>
            <Input
              id="api_url"
              value={customConfig.api_base_url}
              onChange={(e) => setCustomConfig({...customConfig, api_base_url: e.target.value})}
              placeholder="https://api.leadvalidator.dk"
              data-testid="custom-api-url"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="api_token">API Token</Label>
            <Input
              id="api_token"
              type="password"
              value={customConfig.api_token}
              onChange={(e) => setCustomConfig({...customConfig, api_token: e.target.value})}
              placeholder="Din API token"
              data-testid="custom-api-token"
            />
          </div>
          
          <Button 
            onClick={handleTestCustomConfig}
            disabled={testing}
            variant="outline"
            className="w-full"
            data-testid="test-custom-config-btn"
          >
            {testing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4 mr-2" />
            )}
            Test denne konfiguration
          </Button>
          
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg text-amber-700 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              For at ændre den permanente konfiguration skal miljøvariablerne opdateres på serveren.
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminHubhusPage;
